import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useMemo,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabaseClient";
import { logUserActivity } from "@/utils/activityLogger";
import { useNavigate } from "react-router-dom";
import { getCurrentTenantId } from "@/utils/tenantUtils";

// Define return types for auth methods
type LoginResult = { user: User | null; session: Session | null } | null;
type OtpResult = { error: Error | null };

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  authLoading: boolean; // Add this for backward compatibility
  originalUser: User | null;
  isImpersonating: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginWithOtp: (email: string) => Promise<OtpResult>;
  logout: () => Promise<void>;
  impersonateUser: (user: User) => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoading(true);
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error) {
          console.error("Session fetch error:", error);
          // Don't set loading to false immediately on error, retry once
          setTimeout(async () => {
            try {
              const {
                data: { session: retrySession },
                error: retryError,
              } = await supabase.auth.getSession();
              if (retryError) {
                console.error("Session fetch retry error:", retryError);
                setLoading(false);
                return;
              }
              setSession(retrySession);
              setUser(retrySession?.user ?? null);
            } catch (retryCatchError) {
              console.error(
                "Session fetch retry catch error:",
                retryCatchError
              );
            } finally {
              setLoading(false);
            }
          }, 1000);
          return;
        }

        // Check for impersonation state in localStorage
        const storedImpersonation = localStorage.getItem("impersonationState");

        if (storedImpersonation && !isImpersonating) {
          const { originalUser: storedOriginalUser, impersonatedUser } =
            JSON.parse(storedImpersonation);

          setOriginalUser(storedOriginalUser);
          setUser(impersonatedUser);
          setSession(session);
          setIsImpersonating(true);
        } else if (!storedImpersonation) {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error: unknown) {
        console.error("Session fetch error:", error);
        console.error("Error fetching session:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isImpersonating) {
          // Only update state if there's actually a change to prevent unnecessary re-renders
          setSession(prevSession => {
            // Compare session properties to avoid unnecessary updates
            if (!prevSession && !session) return prevSession;
            if (!prevSession && session) return session;
            if (prevSession && !session) return session;
            if (prevSession?.user?.id === session?.user?.id && 
                prevSession?.access_token === session?.access_token &&
                prevSession?.expires_at === session?.expires_at) {
              return prevSession; // No change, prevent re-render
            }
            return session;
          });
          
          setUser(prevUser => {
            const newUser = session?.user ?? null;
            // Don't check avatar_url to avoid unnecessary re-renders
            // The Header component will handle avatar updates via realtime subscription
            
            if (prevUser?.id === newUser?.id && 
                prevUser?.email === newUser?.email) {
              return prevUser; // No change, prevent re-render
            }
            return newUser;
          });
        }
        setLoading(false);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove isImpersonating dependency to prevent infinite loop

  const login = async (
    email: string,
    password: string
  ): Promise<LoginResult> => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Log successful login
      if (data.user) {
        await logUserActivity.login(data.user.id);
      }

      return data.user && data.session
        ? { user: data.user, session: data.session }
        : null;
    } catch (error: unknown) {
      console.error("Login error:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loginWithOtp = async (email: string): Promise<OtpResult> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) return { error };
      alert("Check your email for the login link!");
      return { error: null };
    } catch (error: unknown) {
      return {
        error:
          error instanceof Error ? error : new Error("Unknown error occurred"),
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      // Log logout before signing out
      if (user) {
        await logUserActivity.logout(user.id);
      }

      localStorage.removeItem("impersonationState");
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setOriginalUser(null);
      setIsImpersonating(false);
    } catch (error: unknown) {
      console.error("Error during logout:", error);
    } finally {
      setLoading(false);
    }
  };

  const impersonateUser = async (userToImpersonate: User) => {
    try {
      // Store current user as original user
      const originalUser = user;
      if (!originalUser) {
        throw new Error('No current user found for impersonation');
      }
      setOriginalUser(originalUser);
      
      // Get current session token
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        throw new Error("No active session found");
      }

      // Call the impersonate-user Edge Function
      console.log('Calling impersonate function with:', {
        targetUserId: userToImpersonate.id,
        action: 'start'
      });

      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { 
          targetUserId: userToImpersonate.id,
          action: 'start'
        },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`
        }
      });

      console.log('Function response:', { data, error });

      if (error) {
        console.error("Error calling impersonate function:", error);
        throw new Error(`Function error: ${error.message || 'Failed to start impersonation'}`);
      }

      if (!data) {
        throw new Error('No data returned from function');
      }

      if (!data.success) {
        console.error("Function returned error:", data);
        throw new Error(`Function error: ${data.error || 'Impersonation failed'}`);
      }

      // Store original session for restoration later
      localStorage.setItem("originalSession", JSON.stringify(currentSession));
      localStorage.setItem("impersonationState", JSON.stringify({
        originalUser,
        impersonatedUser: data.session.user
      }));

      // Store original session and user info for logging
      const originalTenantId = await getCurrentTenantId();
      
      // Set the new session using Supabase's setSession
      const { error: setSessionError } = await supabase.auth.setSession(data.session);
      
      if (setSessionError) {
        console.error("Error setting impersonation session:", setSessionError);
        throw setSessionError;
      }

      // Update state first
      setIsImpersonating(true);
      setUser(data.session.user);

      // Log impersonation start after impersonation is set up - using explicit values to avoid session confusion
      try {
        console.log(`[Impersonation] Logging impersonation start: ${originalUser.email || 'unknown'} -> ${data.session.user.email || 'unknown'}`);
        await logUserActivity.impersonationStarted(
          data.session.user.id,
          data.session.user.email || '',
          originalUser.id,
          originalUser.email || '',
          originalTenantId || undefined
        );
        console.log(`[Impersonation] Successfully logged impersonation start`);
      } catch (logError) {
        console.error("Error logging impersonation start:", logError);
        // Don't fail the impersonation if logging fails
      }

      console.log(`Started impersonating user: ${data.session.user.email}`);

      // Redirect to dashboard
      navigate("/app/dashboard");
    } catch (error) {
      console.error("Error impersonating user:", error);
      // Reset impersonation state on error
      setOriginalUser(null);
      setIsImpersonating(false);
      localStorage.removeItem("impersonationState");
      localStorage.removeItem("originalSession");
      throw error;
    }
  };

  const stopImpersonating = async () => {
    try {
      const impersonationState = localStorage.getItem("impersonationState");
      const originalSessionData = localStorage.getItem("originalSession");
      
      if (!impersonationState || !originalSessionData) {
        throw new Error("No impersonation state found");
      }

      const { originalUser } = JSON.parse(impersonationState);
      const originalSession = JSON.parse(originalSessionData);
      
      // Get current impersonated user for logging
      const currentImpersonatedUser = user;

      // Restore the original session
      const { error: setSessionError } = await supabase.auth.setSession(originalSession);
      
      if (setSessionError) {
        console.error("Error restoring original session:", setSessionError);
        throw setSessionError;
      }

      // Log impersonation end - now we're back to original session
      if (currentImpersonatedUser) {
        try {
          console.log(`[Impersonation] Logging impersonation end: ${originalUser.email || 'unknown'} <- ${currentImpersonatedUser.email || 'unknown'}`);
          const tenantId = await getCurrentTenantId();
          await logUserActivity.impersonationEnded(
            currentImpersonatedUser.id,
            currentImpersonatedUser.email || '',
            originalUser.id,
            originalUser.email || '',
            tenantId || undefined
          );
          console.log(`[Impersonation] Successfully logged impersonation end`);
        } catch (logError) {
          console.error("Error logging impersonation end:", logError);
          // Don't fail the restoration if logging fails
        }
      }

      // Clear impersonation state
      localStorage.removeItem("impersonationState");
      localStorage.removeItem("originalSession");
      setOriginalUser(null);
      setIsImpersonating(false);

      // Set the user state back to original user
      setUser(originalUser);

      console.log(`Stopped impersonating, restored session for: ${originalUser.email}`);

      // Redirect to settings page
      navigate("/app/settings");
    } catch (error) {
      console.error("Error stopping impersonation:", error);
      throw error;
    }
  };

  const value = useMemo(() => ({
    session,
    user,
    loading,
    authLoading: loading, // Add this for backward compatibility
    originalUser,
    isImpersonating,
    login,
    loginWithOtp,
    logout,
    impersonateUser,
    stopImpersonating,
  }), [session, user, loading, originalUser, isImpersonating, login, loginWithOtp, logout, impersonateUser, stopImpersonating]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
